import { useState } from 'react'
import { Card, Descriptions, Button, Space, Modal, Form, Input, Message } from '@arco-design/web-react'
import { IconEdit } from '@arco-design/web-react/icon'
import { useTranslation } from 'react-i18next'
import { updateUserInfoApi } from '@/api/user'
import type { User } from '@/types'
import { getFormValidateErrorMessage } from '@/utils/helpers'
import EmailBindModal from './modals/EmailBindModal'

interface ProfileCardProps {
  user: User | null
  onUpdated: () => Promise<void>
}

export default function ProfileCard({ user, onUpdated }: ProfileCardProps) {
  const { t } = useTranslation()
  const [nameOpen, setNameOpen] = useState(false)
  const [emailOpen, setEmailOpen] = useState(false)
  const [nameLoading, setNameLoading] = useState(false)
  const [form] = Form.useForm()

  const saveDisplayName = async () => {
    try {
      const values = await form.validate()
      setNameLoading(true)
      await updateUserInfoApi({ display_name: values.displayName })
      Message.success(t('personal.profile.updateSuccess'))
      setNameOpen(false)
      await onUpdated()
    } catch (err: unknown) {
      if (err instanceof Error && err.message !== t('用户取消了二次验证')) {
        Message.error(getFormValidateErrorMessage(err))
      }
    } finally {
      setNameLoading(false)
    }
  }

  return (
    <>
      <Card className="tt-card !rounded-2xl !border-[var(--console-border)]" title={t('personal.profile.title')}>
        <Descriptions
          column={{ xs: 1, sm: 2 }}
          data={[
            { label: t('personal.profile.username'), value: user?.username || '-' },
            { label: t('personal.profile.displayName'), value: user?.displayName || '-' },
            { label: t('personal.profile.email'), value: user?.email || t('personal.oauth.unbound') },
          ]}
        />
        <Space wrap className="mt-4">
          <Button icon={<IconEdit />} onClick={() => { form.setFieldsValue({ displayName: user?.displayName }); setNameOpen(true) }}>
            {t('personal.profile.editDisplayName')}
          </Button>
          <Button icon={<IconEdit />} onClick={() => setEmailOpen(true)}>
            {user?.email ? t('personal.profile.changeEmail') : t('personal.profile.bindEmail')}
          </Button>
        </Space>
      </Card>

      <Modal
        title={t('personal.profile.editDisplayName')}
        visible={nameOpen}
        onCancel={() => setNameOpen(false)}
        onOk={() => void saveDisplayName()}
        confirmLoading={nameLoading}
      >
        <Form form={form} layout="vertical">
          <Form.Item field="displayName" label={t('personal.profile.displayName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <EmailBindModal
        visible={emailOpen}
        onClose={() => setEmailOpen(false)}
        initialEmail={user?.email}
        onSuccess={onUpdated}
      />
    </>
  )
}
